// TipTap custom node for figures with image drag-and-drop support.
// Renders a drop-zone when no image is set, and the image with caption when populated.

import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import katex from "katex";
import { preprocessForKatex } from "./math-nodes";
import * as pdfjsLib from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker.mjs?url";
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

// ── Global project images store ──────────────────────────────────────────────
// Maps filename → data URL. Populated when user uploads images alongside a .tex file
// or drops/selects an image into a figure node.
const projectImages: Map<string, string> = new Map();

// Listeners notified whenever setProjectImage is called.
// FigureNodeView components subscribe when their src is empty so they self-heal
// as soon as a matching image is loaded (from IndexedDB, Storage, or drop).
const imageLoadListeners = new Set<() => void>();

export function setProjectImage(filename: string, dataUrl: string): void {
    projectImages.set(filename, dataUrl);
    for (const fn of imageLoadListeners) fn();
}

export function getProjectImage(filename: string): string | undefined {
    if (projectImages.has(filename)) return projectImages.get(filename);
    // Try without extension (e.g. "tikz/fig" matches "tikz/fig.pdf")
    const noExt = filename.replace(/\.[^./]+$/, "");
    if (projectImages.has(noExt)) return projectImages.get(noExt);
    // Try basename only
    const base = filename.split("/").pop() ?? filename;
    const baseNoExt = base.replace(/\.[^.]+$/, "");
    // Scan map for any key whose path/basename (with or without ext) matches
    for (const [key, val] of projectImages) {
        if (key === filename) return val;
        const kNoExt = key.replace(/\.[^./]+$/, "");
        const kBase = key.split("/").pop() ?? key;
        const kBaseNoExt = kBase.replace(/\.[^.]+$/, "");
        if (kNoExt === filename || kNoExt === noExt) return val;
        if (kBase === base || kBase === filename) return val;
        if (kBaseNoExt === baseNoExt || kBaseNoExt === base) return val;
    }
    return undefined;
}

export function getAllProjectImages(): Map<string, string> {
    return projectImages;
}

export function clearProjectImages(): void {
    projectImages.clear();
}

/** Read a File as a data URL */
function readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/** Render a caption string that may contain inline $...$ math to HTML. */
function renderCaptionHtml(text: string): string {
    if (!text) return "";
    const parts = text.split(/(\$[^$]+\$)/g);
    return parts.map((part) => {
        if (part.startsWith("$") && part.endsWith("$") && part.length > 2) {
            const math = part.slice(1, -1);
            try {
                return katex.renderToString(preprocessForKatex(math), {
                    displayMode: false,
                    throwOnError: false,
                    strict: "ignore",
                    trust: true,
                });
            } catch {
                return part;
            }
        }
        return part.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    }).join("");
}

// ── Figure NodeView ──────────────────────────────────────────────────────────

function FigureNodeView({ node, updateAttributes, editor, getPos }: NodeViewProps) {
    const src: string = node.attrs.src ?? "";
    const caption: string = node.attrs.caption ?? "";
    const label: string = node.attrs.label ?? "";
    const width: string = node.attrs.width ?? "0.8";
    const filename: string = node.attrs.filename ?? "";

    const [isDragOver, setIsDragOver] = useState(false);
    const [editingCaption, setEditingCaption] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
    const captionRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Self-heal: if src is missing, subscribe to image-load events and update
    // the node attribute as soon as the image is available in the in-memory map.
    // This covers all async load paths (IndexedDB, Firebase Storage, drag-drop).
    useEffect(() => {
        if (src || !filename) return;
        const tryResolve = () => {
            const found = getProjectImage(filename);
            if (found) updateAttributes({ src: found });
        };
        // Try immediately (image may already be in the map)
        tryResolve();
        // Subscribe for future loads
        imageLoadListeners.add(tryResolve);
        return () => { imageLoadListeners.delete(tryResolve); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, filename]);

    // Try to resolve image from project images if src is empty but filename exists
    const resolvedSrc = src || (filename ? getProjectImage(filename) : "") || "";
    const isPdf = resolvedSrc.startsWith("data:application/pdf") || resolvedSrc.endsWith(".pdf");

    // Render first page of a PDF data URL to a PNG preview
    useEffect(() => {
        if (!isPdf || !resolvedSrc) { setPdfPreviewUrl(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const pdf = await pdfjsLib.getDocument(resolvedSrc).promise;
                const page = await pdf.getPage(1);
                const viewport = page.getViewport({ scale: 2.0 });
                const canvas = document.createElement("canvas");
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                const ctx = canvas.getContext("2d")!;
                await page.render({ canvasContext: ctx, canvas, viewport }).promise;
                if (!cancelled) setPdfPreviewUrl(canvas.toDataURL("image/png"));
            } catch {
                if (!cancelled) setPdfPreviewUrl(null);
            }
        })();
        return () => { cancelled = true; };
    }, [isPdf, resolvedSrc]);

    useEffect(() => {
        if (!lightboxOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setLightboxOpen(false); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [lightboxOpen]);

    const handleFile = useCallback(async (file: File) => {
        const isImage = file.type.startsWith("image/");
        const isPdfFile = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
        if (!isImage && !isPdfFile) return;
        if (file.size > 20 * 1024 * 1024) {
            alert("File must be under 20 MB");
            return;
        }
        const dataUrl = await readFileAsDataURL(file);
        // Sanitize filename: replace spaces and non-safe chars so \includegraphics
        // matches what the compile server writes to disk (server applies same logic).
        const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")) : "";
        const base = file.name.slice(0, file.name.length - ext.length);
        const safeName = base.replace(/[^a-zA-Z0-9._\-]/g, "_") + ext;
        setProjectImage(safeName, dataUrl);
        updateAttributes({ src: dataUrl, filename: safeName });
    }, [updateAttributes]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    }, [handleFile]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
    }, [handleFile]);

    const handleCaptionBlur = useCallback(() => {
        setEditingCaption(false);
    }, []);

    const handleCaptionKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === "Escape") {
            setEditingCaption(false);
        }
    }, []);

    const handleDelete = useCallback(() => {
        const pos = typeof getPos === 'function' ? getPos() : undefined;
        if (pos !== undefined) {
            editor.commands.deleteRange({ from: pos, to: pos + node.nodeSize });
            editor.commands.focus();
        }
    }, [editor, getPos, node.nodeSize]);

    const handleReplace = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    return (
        <>
            <NodeViewWrapper data-type="figure-block" className="figure-node">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    style={{ display: "none" }}
                    onChange={handleFileInput}
                />
                {resolvedSrc ? (
                    // ── Image is loaded ──
                    <figure className="figure-container">
                        <div className="figure-image-wrapper">
                            {isPdf && !pdfPreviewUrl ? (
                                <div className="figure-pdf-loading">Rendering PDF…</div>
                            ) : (
                                <img
                                    src={pdfPreviewUrl ?? resolvedSrc}
                                    alt={caption || "Figure"}
                                    style={{ cursor: "zoom-in" }}
                                    className="figure-image"
                                    draggable={false}
                                    onClick={() => setLightboxOpen(true)}
                                />
                            )}
                            <div className="figure-overlay">
                                <button className="figure-overlay-btn" onClick={handleReplace} title="Replace image">
                                    Replace
                                </button>
                                <button className="figure-overlay-btn figure-overlay-btn--danger" onClick={handleDelete} title="Delete figure">
                                    Delete
                                </button>
                            </div>
                        </div>
                        <figcaption className="figure-caption">
                            {editingCaption ? (
                                <input
                                    ref={captionRef}
                                    type="text"
                                    className="figure-caption-input"
                                    value={caption}
                                    onChange={(e) => updateAttributes({ caption: e.target.value })}
                                    onBlur={handleCaptionBlur}
                                    onKeyDown={handleCaptionKeyDown}
                                    autoFocus
                                    placeholder="Enter caption…"
                                />
                            ) : (
                                <span
                                    className="figure-caption-text"
                                    onClick={() => setEditingCaption(true)}
                                    title="Click to edit caption"
                                    dangerouslySetInnerHTML={caption
                                        ? { __html: renderCaptionHtml(caption) }
                                        : undefined
                                    }
                                >
                                    {caption ? undefined : "Click to add caption"}
                                </span>
                            )}
                        </figcaption>
                    </figure>
                ) : (
                    // ── Drop zone ──
                    <div
                        className={`figure-dropzone ${isDragOver ? "figure-dropzone--active" : ""}`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={handleClick}
                    >
                        <div className="figure-dropzone-content">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <polyline points="21 15 16 10 5 21" />
                            </svg>
                            <p className="figure-dropzone-text">
                                {isDragOver ? "Drop image here" : "Drag & drop an image, or click to browse"}
                            </p>
                        </div>
                    </div>
                )}
            </NodeViewWrapper>
            {lightboxOpen && createPortal(
                <div
                    className="figure-lightbox-overlay"
                    onClick={() => setLightboxOpen(false)}
                    role="dialog"
                    aria-modal="true"
                >
                    <img
                        src={pdfPreviewUrl ?? resolvedSrc}
                        alt={caption || "Figure"}
                        className="figure-lightbox-img"
                        onClick={(e) => e.stopPropagation()}
                    />
                    {caption && <p className="figure-lightbox-caption" dangerouslySetInnerHTML={{ __html: renderCaptionHtml(caption) }} />}
                    <button className="figure-lightbox-close" onClick={() => setLightboxOpen(false)} aria-label="Close">&times;</button>
                </div>,
                document.body
            )}
        </>
    );
}

// ── FigureBlock TipTap Extension ─────────────────────────────────────────────

export const FigureBlock = Node.create({
    name: "figureBlock",
    group: "block",
    atom: true,
    draggable: true,

    addAttributes() {
        return {
            src: { default: "" },
            filename: { default: "" },
            caption: { default: "" },
            label: { default: "" },
            width: { default: "0.8" },
        };
    },

    parseHTML() {
        return [{ tag: 'figure[data-type="figure-block"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ["figure", mergeAttributes(HTMLAttributes, { "data-type": "figure-block" }), 0];
    },

    addNodeView() {
        return ReactNodeViewRenderer(FigureNodeView, {
            stopEvent: ({ event }) => {
                // Let all events inside the figure node be handled by React
                if (event.type === 'mousedown' || event.type === 'drop' || event.type === 'dragover' || event.type === 'dragleave') return true;
                if (event.type === 'keydown' || event.type === 'keyup' || event.type === 'keypress') {
                    const target = event.target as HTMLElement;
                    if (target.closest('.figure-caption-input') || target.closest('input')) {
                        return true;
                    }
                }
                return false;
            },
        });
    },
});
